import { DataProvider, HttpError } from "@refinedev/core";
import { axiosInstance, generateSort, generateFilter } from "functions";
import { AxiosInstance } from "axios";
import { stringify } from "query-string";
import { IAgent } from "../interfaces";

type MethodTypes = "get" | "delete" | "head" | "options";
type MethodTypesWithBody = "post" | "put" | "patch";

export const agentDataProvider = (
  apiUrl: string,
  httpClient: AxiosInstance = axiosInstance
): Omit<
  Required<DataProvider>,
  "createMany" | "updateMany" | "deleteMany"
> => ({
  getList: async ({ resource, pagination, filters, sorters, meta }) => {
    const url = `${apiUrl}/${resource}`;

    const { current = 1, pageSize = 10, mode = "server" } = pagination ?? {};

    const { headers: headersFromMeta, method } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? "get";

    const queryFilters = generateFilter(filters);

    const query: {
      page?: number;
      size?: number;
    } = {};

    if (mode === "server") {
      query.page = current - 1;
      query.size = pageSize;
    }

    const combinedQuery = { sorters: sorters, filters: filters, ...query };
    const urlWithQuery = `${url}/q?params=${encodeURIComponent(JSON.stringify(combinedQuery))}`;

    var { data, headers } = await httpClient[requestMethod](urlWithQuery, {
      headers: headersFromMeta,
    });

    const total = +data.totalElements;
    data = data.content;

    return {
      data,
      total: total || data.length,
    };
  },

  getMany: async ({ resource, ids, meta }) => {
    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? "get";

    const { data } = await httpClient[requestMethod](
      `${apiUrl}/${resource}?${stringify({ id: ids })}`,
      { headers }
    );

    return {
      data,
    };
  },

  create: async ({ resource, variables, meta }) => { console.log("create", resource, variables, meta);
//    const url = `${apiUrl}/${resource}`;
    const url = `${apiUrl}/agent`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? "post";

    const agentVariable = variables as IAgent;

    if (agentVariable.newPassword !== "******" && agentVariable.newPassword !== agentVariable.passwordCheck) {
      const error: HttpError = {
        message: "비밀번호가 일치하지 않습니다.",
        statusCode: 400,
      };
      return Promise.reject(error);
    }

    let formData = new FormData();

    formData.append("a_name", agentVariable.name);
    formData.append("a_ph", agentVariable.phoneNumber);
    formData.append("a_code", agentVariable.code);
    formData.append("a_address", agentVariable.a_address);
    formData.append("a_hasCar", agentVariable.a_hasCar === "CAR" ? "true" : "false");
    formData.append("nickname", agentVariable.loginId);
    formData.append("pwd", agentVariable.newPassword);

    const { data } = await httpClient[requestMethod](url, formData, {
      headers,
    });

    return {
      data,
    };
  },

  update: async ({ resource, id, variables, meta }) => { console.log("update", resource, id, variables, meta);
    // const url = `${apiUrl}/${resource}/${id}`;
    const url = `${apiUrl}/agent`;

    const { headers, method } = meta ?? {};
    const requestMethod = "patch";

    const agentVariable = variables as IAgent;

    if (agentVariable.newPassword !== "******" && agentVariable.newPassword !== agentVariable.passwordCheck) {
      const error: HttpError = {
        message: "비밀번호가 일치하지 않습니다.",
        statusCode: 400,
      };
      return Promise.reject(error);
    }

    let formData = new FormData();

    formData.append("agent_id", id.toString());

    if (agentVariable.a_receiveDate) {
        formData.append("a_receiveDate", agentVariable.a_receiveDate);
    } else {
        formData.append("a_receiveDate", "2023-01-01");
    }

    if (agentVariable.a_dispatchDate) {
        formData.append("a_dispatchDate", agentVariable.a_dispatchDate);
    } else {
        formData.append("a_dispatchDate", "2023-01-01");
    }

    formData.append("a_name", agentVariable.name);
    formData.append("a_ph", agentVariable.phoneNumber);
    formData.append("a_code", agentVariable.code);
    formData.append("a_address", agentVariable.a_address);
    formData.append("a_equipment", agentVariable.a_equipment);
    formData.append("a_hasDocument1", agentVariable.a_hasDocument1 === "true" ? "true" : "false");
    formData.append("a_hasDocument2", agentVariable.a_hasDocument2 === "true" ? "true" : "false");
    formData.append("a_hasDocument3", agentVariable.a_hasDocument3 === "true" ? "true" : "false");
    formData.append("a_hasDocument4", agentVariable.a_hasDocument4 === "true" ? "true" : "false");
    formData.append("a_hasDocument5", agentVariable.a_hasDocument5 === "true" ? "true" : "false");
    formData.append("a_comment", agentVariable.a_comment);
    formData.append("a_hasCar", agentVariable.a_hasCar === "CAR" ? "true" : "false");
    formData.append("a_status", agentVariable.a_status === "WORK" ? "true" : "false");
    formData.append("nickname", agentVariable.loginId);
    formData.append("pwd", agentVariable.newPassword);

    const { data } = await httpClient[requestMethod](url, formData, {
      headers,
    });

    return {
      data,
    };
  },

  getOne: async ({ resource, id, meta }) => { console.log("getOne", resource, id, meta);
    const url = `${apiUrl}/${resource}/${id}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? "get";

    const { data } = await httpClient[requestMethod](url, { headers });

    return {
      data,
    };
  },

  deleteOne: async ({ resource, id, variables, meta }) => { console.log("deleteOne", resource, id, variables, meta);
    const url = `${apiUrl}/${resource}/${id}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? "delete";

    const { data } = await httpClient[requestMethod](url, {
      data: variables,
      headers,
    });

    return {
      data,
    };
  },

  getApiUrl: () => {
    return apiUrl;
  },

  custom: async ({
    url,
    method,
    filters,
    sorters,
    payload,
    query,
    headers,
  }) => {
    let requestUrl = `${url}?`;

    if (sorters) {
      const generatedSort = generateSort(sorters);
      if (generatedSort) {
        const { _sort, _order } = generatedSort;
        const sortQuery = {
          _sort: _sort.join(","),
          _order: _order.join(","),
        };
        requestUrl = `${requestUrl}&${stringify(sortQuery)}`;
      }
    }

    if (filters) {
      const filterQuery = generateFilter(filters);
      requestUrl = `${requestUrl}&${stringify(filterQuery)}`;
    }

    if (query) {
      requestUrl = `${requestUrl}&${stringify(query)}`;
    }

    let axiosResponse;
    switch (method) {
      case "put":
      case "post":
      case "patch":
        axiosResponse = await httpClient[method](url, payload, {
          headers,
        });
        break;
      case "delete":
        axiosResponse = await httpClient.delete(url, {
          data: payload,
          headers: headers,
        });
        break;
      default:
        axiosResponse = await httpClient.get(requestUrl, {
          headers,
        });
        break;
    }

    const { data } = axiosResponse;

    return Promise.resolve({ data });
  },
});
